import type { ReactNode } from 'react';

interface StartupErrorProps {
  title: string;
  children: ReactNode;

  /**
   * Offers to clear the cached auth state and reload. Worth showing for MSAL startup failures,
   * which are often a stale or half-written redirect state in sessionStorage rather than anything
   * wrong with the deployment.
   */
  offerSessionReset?: boolean;
}

/**
 * Shown when the app cannot start at all.
 *
 * This renders without MSAL, which is the point: if something throws while the auth session is
 * being set up, React never mounts and the user gets a blank white page with the reason visible
 * only in the browser console. A blank page is the worst possible failure mode for a tool people
 * are told to go and use.
 */
export function StartupError({ title, children, offerSessionReset = false }: StartupErrorProps) {
  return (
    <div className="page-message page-message--error">
      <h2>{title}</h2>
      {children}

      {offerSessionReset && (
        <p>
          <button
            type="button"
            className="button button--primary"
            onClick={() => {
              window.sessionStorage.clear();
              window.localStorage.clear();
              window.location.replace(window.location.origin);
            }}
          >
            Clear the saved session and try again
          </button>
        </p>
      )}
    </div>
  );
}
